import React, { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useMutation } from '@tanstack/react-query';
import { ScoreWidget } from '@traquility/ui/diary';
import type { ScoreField } from '@traquility/ui/diary';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

interface ScoreWidgetData {
  id: string;
  name: string;
  scoreFields: ScoreField[];
}

interface ScoreWidgetContainerProps {
  /** The widget data passed from Rails */
  widget: ScoreWidgetData;
  /** The diary date for API calls (YYYY-MM-DD) */
  diaryDate: string;
  /** Initial field values from Rails (fieldId -> value, null means not selected) */
  initialValues: Record<string, number | null>;
  /** GraphQL endpoint URL */
  graphqlEndpoint: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** URL for edit/manage action */
  editUrl?: string;
  /** URL for delete action */
  deleteUrl?: string;
  /** Labels for dropdown actions */
  labels?: {
    manageWidget?: string;
    deleteWidget?: string;
    areYouSure?: string;
  };
}

// GraphQL mutation for triggering numeric activity
const TRIGGER_NUMERIC_ACTIVITY = `
  mutation TriggerNumericActivity($diaryDate: String!, $fieldId: ID!, $value: Int!, $triggerAction: String) {
    triggerNumericActivity(input: { diaryDate: $diaryDate, fieldId: $fieldId, value: $value, triggerAction: $triggerAction }) {
      activity {
        id
        active
        value
        field {
          id
          name
        }
      }
    }
  }
`;

function ScoreWidgetInner({
  widget,
  diaryDate,
  initialValues,
  graphqlEndpoint,
  csrfToken,
  editUrl,
  deleteUrl,
  labels,
}: ScoreWidgetContainerProps) {
  const [fieldValues, setFieldValues] = React.useState<Record<string, number | null>>(initialValues);

  // Mutation for updating numeric field value via GraphQL
  const mutation = useMutation({
    mutationFn: async ({ fieldId, value }: { fieldId: string; value: number }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: TRIGGER_NUMERIC_ACTIVITY,
          variables: {
            diaryDate,
            fieldId,
            value,
            triggerAction: 'set_value',
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update field value');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.triggerNumericActivity.activity;
    },
    onSuccess: (data, variables) => {
      // Update local state on success
      setFieldValues((prev) => ({
        ...prev,
        [variables.fieldId]: variables.value,
      }));
    },
  });

  const handleFieldValueChange = useCallback((fieldId: string, value: number) => {
    // Optimistically update the local state
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    // Send the mutation
    mutation.mutate({ fieldId, value });
  }, [mutation]);

  return (
    <ScoreWidget
      id={widget.id}
      name={widget.name}
      scoreFields={widget.scoreFields}
      fieldValues={fieldValues}
      onFieldValueChange={handleFieldValueChange}
      isLoading={mutation.isPending}
      editUrl={editUrl}
      deleteUrl={deleteUrl}
      labels={labels}
      className="score-widget-container"
    />
  );
}

/**
 * ScoreWidgetContainer - Entry point for rendering score widgets in Rails
 *
 * This component wraps the shared ScoreWidget with:
 * - React Query provider
 * - GraphQL mutation handling
 * - CSRF token handling for Rails
 */
export function ScoreWidgetContainer(props: ScoreWidgetContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ScoreWidgetInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountScoreWidget(
  container: HTMLElement,
  props: ScoreWidgetContainerProps
) {
  const root = createRoot(container);
  root.render(<ScoreWidgetContainer {...props} />);
  return root;
}

export default ScoreWidgetContainer;
